import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { pointsBadgeClass } from "../lib/badges";
import { useI18n } from "../i18n";
import { toast } from "sonner";
import Countdown from "../components/Countdown";
import TeamCrest from "../components/TeamCrest";
import ScoreStepper from "../components/ScoreStepper";
import { teamShort } from "./pool/utils";
import { Loader2, Lock, Calendar, Save } from "lucide-react";

function fmtTime(iso, lang) {
    if (!iso) return "—";
    try {
        const locale = lang === "sv" ? "sv-SE" : undefined;
        return new Date(iso).toLocaleString(locale);
    } catch { return iso; }
}

function isKickedOff(iso) {
    return iso && new Date(iso).getTime() <= Date.now();
}

export default function MatchDetail() {
    const { matchId } = useParams();
    const { t, lang } = useI18n();
    const nav = useNavigate();
    const location = useLocation();
    const preferredPoolId = location.state?.poolId || null;
    const [match, setMatch] = useState(null);
    const [teamMap, setTeamMap] = useState({});
    const [pools, setPools] = useState([]);
    const [selectedPool, setSelectedPool] = useState("");
    const [predictedBracket, setPredictedBracket] = useState({});
    const [predictions, setPredictions] = useState([]);
    const [locked, setLocked] = useState(false);
    const [loading, setLoading] = useState(true);

    // Local prediction-editor state
    const [myPrediction, setMyPrediction] = useState(null);
    const [h, setH] = useState(0);
    const [a, setA] = useState(0);
    const [pen, setPen] = useState("");
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const [mRes, tRes, pRes] = await Promise.all([
                    api.get(`/matches/${matchId}`),
                    api.get(`/teams`),
                    api.get(`/pools/mine`),
                ]);
                setMatch(mRes.data);
                const tmap = {};
                tRes.data.forEach((tm) => { tmap[tm.id] = tm; });
                setTeamMap(tmap);
                setPools(pRes.data);
                // Prefer the pool the user came from (location.state.poolId)
                // so the editor binds to the active pool immediately.
                const fromState = pRes.data.find((p) => p.id === preferredPoolId);
                if (fromState) setSelectedPool(fromState.id);
                else if (pRes.data.length > 0) setSelectedPool(pRes.data[0].id);
            } catch (e) {
                toast.error(formatApiError(e.response?.data?.detail) || e.message);
            } finally { setLoading(false); }
        })();
    }, [matchId, preferredPoolId]);

    // Auto-refresh while a match is LIVE so the score / minute / details
    // panel update without the user pressing reload. Polls the single
    // match doc every 30s — light on the backend (a single Mongo lookup)
    // and only runs while we observe status='live'.
    useEffect(() => {
        if (match?.status !== "live") return;
        const interval = setInterval(async () => {
            try {
                const { data } = await api.get(`/matches/${matchId}`);
                setMatch(data);
            } catch { /* ignore — next tick will retry */ }
        }, 30_000);
        return () => clearInterval(interval);
    }, [match?.status, matchId]);

    useEffect(() => {
        if (!selectedPool || !matchId) return;
        (async () => {
            try {
                const { data } = await api.get(`/pools/${selectedPool}/matches/${matchId}/predictions`);
                setPredictions(data.predictions || []);
                setLocked(!!data.locked);
            } catch (e) {
                toast.error(formatApiError(e.response?.data?.detail) || e.message);
            }
            // Load current user's prediction for this match (if any).
            try {
                const { data } = await api.get(`/pools/${selectedPool}/predictions/mine`);
                const mine = (data || []).find((p) => p.match_id === matchId) || null;
                setMyPrediction(mine);
                setH(mine?.home_goals ?? 0);
                setA(mine?.away_goals ?? 0);
                setPen(mine?.pen_winner_team_id ?? "");
                setDirty(false);
            } catch { /* non-fatal */ }
            // Predicted-bracket overlay for TBD knockout slots
            try {
                const { data } = await api.get(`/pools/${selectedPool}/predicted-bracket`);
                setPredictedBracket(data || {});
            } catch { /* non-fatal */ }
        })();
    }, [selectedPool, matchId]);

    const ps = predictedBracket[matchId] || {};
    const homeId = match?.home_team_id || ps.home_team_id || null;
    const awayId = match?.away_team_id || ps.away_team_id || null;
    const selectedPoolName = pools.find((p) => p.id === selectedPool)?.name || "";
    const isKnockout = match?.stage === "knockout";
    const isDraw = h === a;
    // The pen picker shows for knockout draws whenever both teams are
    // known — either from the real bracket OR from the user's predicted
    // overlay (so the user can still pick a pen winner pre-resolution).
    const showPen = isKnockout && isDraw && !!homeId && !!awayId;
    const matchLocked = isKickedOff(match?.kickoff);
    const isFinished = match?.status === "finished" && match?.home_goals != null;
    const canEdit = !!selectedPool && !matchLocked && !isFinished;

    const hasMyPred = !!myPrediction;

    const savePrediction = async () => {
        if (!selectedPool || !match) return;
        setSaving(true);
        try {
            const { data } = await api.post(`/pools/${selectedPool}/predictions`, {
                match_id: match.id,
                home_goals: h,
                away_goals: a,
                pen_winner_team_id: showPen ? pen : null,
            });
            setMyPrediction(data);
            setDirty(false);
            toast.success(t("match.pred.saved"));
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally { setSaving(false); }
    };

    const myPenWinnerName = useMemo(() => {
        if (!myPrediction?.pen_winner_team_id) return null;
        return teamMap[myPrediction.pen_winner_team_id]?.name;
    }, [myPrediction, teamMap]);

    if (loading) return <div className="flex items-center justify-center py-32"><Loader2 className="h-6 w-6 animate-spin text-[var(--accent-hex)]" /></div>;
    if (!match) return <div className="p-8 text-center text-[var(--text-secondary)]">404</div>;

    const home = teamMap[match.home_team_id];
    const away = teamMap[match.away_team_id];
    const homePredicted = !home && ps.home_team_id ? teamMap[ps.home_team_id] : null;
    const awayPredicted = !away && ps.away_team_id ? teamMap[ps.away_team_id] : null;
    const finished = isFinished;
    const isLive = match.status === "live";

    // Pretty-print the upstream last-updated stamp into a relative "x min ago".
    const lastUpdatedLabel = (() => {
        if (!match.last_updated_upstream) return null;
        const dt = new Date(match.last_updated_upstream);
        const diffSec = Math.round((Date.now() - dt.getTime()) / 1000);
        if (diffSec < 60) return t("match.live.updated_seconds", { n: diffSec });
        if (diffSec < 3600) return t("match.live.updated_minutes", { n: Math.round(diffSec / 60) });
        return dt.toLocaleString(lang);
    })();

    return (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6" data-testid="match-page">
            <button onClick={() => nav(-1)} className="text-xs uppercase tracking-widest font-bold text-[var(--text-secondary)] hover:text-[var(--text-primary)] mb-4" data-testid="back-btn">{t("match.back")}</button>

            <div className="rounded-lg border border-[var(--border-base)] bg-[var(--bg-card)] p-6 mb-6">
                <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-[var(--text-muted)] mb-2">
                    <Calendar className="h-3 w-3" /> {match.round} · {fmtTime(match.kickoff, lang)}
                    {match.official_number != null && (
                        <span
                            data-testid="match-official-number"
                            className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-[var(--accent-hex)]/15 text-[var(--accent-hex)] font-mono font-extrabold tabular-nums"
                        >
                            {t("match.number.label")} {match.official_number}
                        </span>
                    )}
                </div>
                <div className="flex items-center justify-around py-6">
                    <div className="flex-1 text-center">
                        <div className="mb-1 flex justify-center">
                            <TeamCrest logo={home?.logo || homePredicted?.logo} name={home?.name || homePredicted?.name} size="lg" />
                        </div>
                        <div className={`font-heading text-xl sm:text-2xl font-bold uppercase tracking-tight ${homePredicted ? "text-amber-400 italic" : "text-[var(--text-primary)]"}`}>
                            {homePredicted && <span className="mr-1" aria-hidden="true">💭</span>}
                            {home?.name || homePredicted?.name || "TBD"}
                        </div>
                    </div>
                    <div className="px-4 text-center">
                        {finished ? (
                            <div className="font-heading text-5xl font-extrabold text-[var(--accent-hex)] tabular-nums">
                                {match.home_goals}–{match.away_goals}
                            </div>
                        ) : isLive ? (
                            <div className="flex flex-col items-center">
                                <div className="font-heading text-5xl font-extrabold text-[var(--danger)] tabular-nums leading-none">
                                    {match.home_goals ?? 0}–{match.away_goals ?? 0}
                                </div>
                                <div className="flex items-center gap-1.5 mt-2">
                                    <span className="inline-block h-2 w-2 rounded-full bg-[var(--danger)] animate-pulse" />
                                    <span className="text-[10px] uppercase tracking-widest font-extrabold text-[var(--danger)]" data-testid="live-clock">
                                        {match.minute != null ? `${match.minute}'` : t("match.live")}{match.injury_time ? ` +${match.injury_time}` : ""}
                                    </span>
                                </div>
                            </div>
                        ) : (
                            <div>
                                <Countdown kickoff={match.kickoff} />
                                <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--text-muted)] mt-2">vs</div>
                            </div>
                        )}
                    </div>
                    <div className="flex-1 text-center">
                        <div className="mb-1 flex justify-center">
                            <TeamCrest logo={away?.logo || awayPredicted?.logo} name={away?.name || awayPredicted?.name} size="lg" />
                        </div>
                        <div className={`font-heading text-xl sm:text-2xl font-bold uppercase tracking-tight ${awayPredicted ? "text-amber-400 italic" : "text-[var(--text-primary)]"}`}>
                            {awayPredicted && <span className="mr-1" aria-hidden="true">💭</span>}
                            {away?.name || awayPredicted?.name || "TBD"}
                        </div>
                    </div>
                </div>
                {match.venue && (
                    <div className="text-center text-xs text-[var(--text-secondary)] mt-2">{match.venue}{match.city ? `, ${match.city}` : ""}</div>
                )}
            </div>

            {/* Live / finished match details — only renders when there's actual
                upstream data to show (score breakdown, referee, last sync etc.). */}
            {(isLive || finished) && (
                <div className="rounded-lg border border-[var(--border-base)] bg-[var(--bg-card)] p-5 mb-6" data-testid="match-live-details">
                    <h3 className="font-heading text-xs uppercase tracking-[0.25em] font-bold text-[var(--text-muted)] mb-4">
                        {t("match.live.details")}
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                        {/* Status pill */}
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-[var(--text-muted)] font-bold">{t("match.live.status")}</div>
                            <div className={`text-sm font-extrabold uppercase tracking-wide mt-1 ${isLive ? "text-[var(--danger)]" : "text-[var(--accent-hex)]"}`}
                                data-testid="match-live-status">
                                {isLive
                                    ? (match.minute != null ? `${match.minute}'${match.injury_time ? ` +${match.injury_time}` : ""}` : t("match.live"))
                                    : t("match.live.finished")}
                            </div>
                        </div>
                        {/* Half-time */}
                        {match.halftime_home_goals != null && (
                            <div>
                                <div className="text-[10px] uppercase tracking-widest text-[var(--text-muted)] font-bold">{t("match.live.halftime")}</div>
                                <div className="text-sm font-bold mt-1 tabular-nums" data-testid="match-live-ht">
                                    {match.halftime_home_goals}–{match.halftime_away_goals}
                                </div>
                            </div>
                        )}
                        {/* Full-time (90 min) */}
                        {match.regulation_home_goals != null && (
                            <div>
                                <div className="text-[10px] uppercase tracking-widest text-[var(--text-muted)] font-bold">{t("match.live.full_time")}</div>
                                <div className="text-sm font-bold mt-1 tabular-nums" data-testid="match-live-ft">
                                    {match.regulation_home_goals}–{match.regulation_away_goals}
                                </div>
                            </div>
                        )}
                        {/* Extra time */}
                        {match.extra_time_home_goals != null && (
                            <div>
                                <div className="text-[10px] uppercase tracking-widest text-[var(--text-muted)] font-bold">{t("match.live.extra_time")}</div>
                                <div className="text-sm font-bold mt-1 tabular-nums text-amber-400" data-testid="match-live-et">
                                    {match.extra_time_home_goals}–{match.extra_time_away_goals}
                                </div>
                            </div>
                        )}
                        {/* Penalties */}
                        {match.penalties_home_goals != null && (
                            <div>
                                <div className="text-[10px] uppercase tracking-widest text-[var(--text-muted)] font-bold">{t("match.live.penalties")}</div>
                                <div className="text-sm font-bold mt-1 tabular-nums text-amber-400" data-testid="match-live-pens">
                                    {match.penalties_home_goals}–{match.penalties_away_goals}
                                </div>
                            </div>
                        )}
                        {/* Phase */}
                        {match.duration && (
                            <div>
                                <div className="text-[10px] uppercase tracking-widest text-[var(--text-muted)] font-bold">{t("match.live.phase")}</div>
                                <div className="text-sm font-bold mt-1 capitalize" data-testid="match-live-phase">
                                    {match.duration.toLowerCase().replace(/_/g, " ")}
                                </div>
                            </div>
                        )}
                        {/* Referee */}
                        {match.referee_name && (
                            <div className="col-span-2">
                                <div className="text-[10px] uppercase tracking-widest text-[var(--text-muted)] font-bold">{t("match.live.referee")}</div>
                                <div className="text-sm font-bold mt-1" data-testid="match-live-referee">
                                    {match.referee_name}
                                    {match.referee_nationality && (
                                        <span className="ml-1 text-[var(--text-muted)] font-normal">({match.referee_nationality})</span>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    {lastUpdatedLabel && (
                        <p className="text-[10px] uppercase tracking-widest text-[var(--text-muted)] font-bold mt-4" data-testid="match-live-updated">
                            {t("match.live.updated_at")} {lastUpdatedLabel}
                        </p>
                    )}
                </div>
            )}

            {/* Inline prediction editor — visible only when user has a pool and
                the match is still open. */}
            {canEdit && pools.length > 0 && (
                <div className="rounded-lg border border-[var(--border-base)] bg-[var(--bg-card)] p-6 mb-6" data-testid="match-pred-editor">
                    <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                        <h3 className="font-heading text-xl font-bold uppercase tracking-tight text-[var(--text-primary)]">
                            {t("match.pred.title")}
                        </h3>
                        {pools.length > 1 && selectedPoolName && (
                            <span className="text-[10px] uppercase tracking-widest font-bold text-[var(--accent-hex)]" data-testid="match-pred-active-pool">
                                {selectedPoolName}
                            </span>
                        )}
                    </div>
                    <div className="flex items-center justify-center gap-3 mb-3">
                        <ScoreStepper testid={`md-home-${match.id}`} value={h} onChange={(v) => { setH(v); setDirty(true); }} />
                        <span className="text-[var(--text-muted)] font-heading text-2xl">:</span>
                        <ScoreStepper testid={`md-away-${match.id}`} value={a} onChange={(v) => { setA(v); setDirty(true); }} />
                    </div>

                    {showPen && (
                        <div className="mt-3 p-3 rounded border border-amber-500/40 bg-amber-500/5" data-testid="md-pen-picker">
                            <label className="block text-[10px] uppercase tracking-widest font-bold text-amber-400 mb-2" htmlFor="md-pen-select">
                                {t("knock.pen.title")}
                            </label>
                            <select
                                id="md-pen-select"
                                data-testid="md-pen-select"
                                value={pen || ""}
                                onChange={(e) => { setPen(e.target.value); setDirty(true); }}
                                className="w-full bg-[var(--bg-base)] border border-amber-500/50 text-[var(--text-primary)] text-sm font-extrabold uppercase tracking-widest px-3 py-2 rounded focus:outline-none focus:border-amber-500"
                            >
                                <option value="">{t("knock.pen.choose")}</option>
                                <option value={homeId}>{teamShort(teamMap[homeId])} — {teamMap[homeId]?.name || "?"}</option>
                                <option value={awayId}>{teamShort(teamMap[awayId])} — {teamMap[awayId]?.name || "?"}</option>
                            </select>
                        </div>
                    )}

                    <div className="mt-4 flex items-center justify-end gap-3">
                        {hasMyPred && !dirty && (
                            <span className="text-[10px] uppercase tracking-widest font-bold text-[var(--text-muted)]" data-testid="md-pred-status">
                                {t("match.pred.saved_label")}
                            </span>
                        )}
                        <button
                            type="button"
                            onClick={savePrediction}
                            disabled={saving || (showPen && !pen) || (hasMyPred && !dirty)}
                            data-testid="md-pred-save"
                            className="inline-flex items-center gap-1 bg-[var(--accent-hex)] text-[var(--accent-text)] text-xs font-extrabold uppercase tracking-widest px-4 py-2 rounded hover:bg-[var(--accent-hex-hover)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                            <Save className="h-3.5 w-3.5" />
                            {saving ? "…" : hasMyPred ? t("match.pred.update") : t("match.pred.save")}
                        </button>
                    </div>
                </div>
            )}

            <div className="rounded-lg border border-[var(--border-base)] bg-[var(--bg-card)] p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-heading text-xl font-bold uppercase tracking-tight text-[var(--text-primary)]">{t("match.pool_picks")}</h3>
                    {pools.length > 1 && selectedPoolName && (
                        <span className="text-[10px] uppercase tracking-widest font-bold text-[var(--accent-hex)]" data-testid="match-pool-active">
                            {selectedPoolName}
                        </span>
                    )}
                </div>

                {pools.length === 0 ? (
                    <p className="text-sm text-[var(--text-secondary)]">{t("match.no_pool")} <Link to="/dashboard" className="text-[var(--accent-hex)] hover:underline">{t("match.create_one")}</Link>.</p>
                ) : !locked ? (
                    <div className="text-center py-6">
                        <Lock className="h-8 w-8 text-[#FF3B30] mx-auto mb-2" />
                        <p className="text-sm text-[var(--text-secondary)]">
                            {t("match.locked_hint")}
                        </p>
                        {hasMyPred && (
                            <div className="mt-4 text-sm text-[var(--text-primary)]" data-testid="my-pick-preview">
                                {t("match.your_pick")} <span className="font-heading font-extrabold text-[var(--accent-hex)] text-xl">{myPrediction.home_goals}–{myPrediction.away_goals}</span>
                                {myPenWinnerName && (
                                    <span className="ml-2 text-[10px] uppercase tracking-widest font-bold text-amber-400">
                                        · {t("match.pen.winner")}: {myPenWinnerName}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                ) : predictions.length === 0 ? (
                    <p className="text-sm text-[var(--text-secondary)]">{t("match.no_picks")}</p>
                ) : (
                    <div className="space-y-2" data-testid="match-predictions-list">
                        {predictions.map((p) => {
                            const penName = p.pen_winner_team_id ? (teamMap[p.pen_winner_team_id]?.name || "?") : null;
                            return (
                                <div key={p.id} className="flex items-center justify-between p-3 rounded-md border border-[var(--border-muted)] bg-[var(--bg-base)]" data-testid={`mp-${p.user_id}`}>
                                    <div className="font-heading text-base font-bold uppercase tracking-tight text-[var(--text-primary)]">{p.user_name}</div>
                                    <div className="flex items-center gap-3 flex-wrap justify-end">
                                        <div className="text-right">
                                            <div className="font-heading font-extrabold text-[var(--text-primary)] text-lg tabular-nums">{p.home_goals}–{p.away_goals}</div>
                                            {penName && (
                                                <div className="text-[10px] uppercase tracking-widest font-bold text-amber-400" data-testid={`mp-pen-${p.user_id}`}>
                                                    {t("match.pen.winner")}: {penName}
                                                </div>
                                            )}
                                        </div>
                                        <span className={`text-[10px] font-extrabold uppercase tracking-widest px-2 py-1 rounded ${pointsBadgeClass(p.points || 0)}`}>{t("fix.points", { n: p.points })}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
